/**
 * @description Heatmap component
 * @file
 * @copyright © Copyright IBI Group., created 2019. All use, disclosure, and/or reproduction of this material is prohibited unless authorized in writing. All rights reserved.
 * @module components/Heatmap
 */

import React from 'react';
import PropTypes from 'prop-types';
import SimpleBar from 'simplebar-react';

import 'simplebar/dist/simplebar.min.css';

import ResetContext from '../context/ResetContext';

import HeatmapHeader from './HeatmapHeader';
import HeatmapPeriod from './HeatmapPeriod';

/**
 * @description The Heatmap component.
 * @class
 * @extends {React.Component}
 */
class Heatmap extends React.Component {
    
    /**
     * @inheritdoc
     */
    constructor(props) {
        super(props);
        this.heatmapContainer = React.createRef();
        this.simpleBarRef = React.createRef();

        this.state = {
            isExpanded: false,
            isExpanding: false,
            showTimeBins: false,
            heatmapHeight: props.heatmapDragService.minimumHeatmapHeightLive
        };
    }

    /**
     * @inheritdoc
     */
    shouldComponentUpdate(nextProps, nextState) {
        return (
            JSON.stringify(this.props.periods) !==
                JSON.stringify(nextProps.periods) ||
            JSON.stringify(this.props.links) !==
                JSON.stringify(nextProps.links) ||
            this.props.zoomLevel !== nextProps.zoomLevel ||
            this.props.distance !== nextProps.distance ||
            this.state.isExpanded !== nextState.isExpanded ||
            this.props.isLive !== nextProps.isLive ||
            this.state.heatmapHeight !== nextState.heatmapHeight ||
            this.state.showTimeBins !== nextState.showTimeBins
        );
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        this.resetHeight();
        this.context(this.props.id, this.resetHeight.bind(this));
    }

    /**
     * @inheritdoc
     */
    componentDidUpdate(prevProps) {
        // Below code is to recalculate SimpleBar after changing zoom level.
        // Needs setTimeout in order to allow for setting state and rendering changes before recalculating.
        if (prevProps.zoomLevel !== this.props.zoomLevel) {
            this.recalcTimeout = setTimeout(
                () =>
                    this.simpleBarRef.current &&
                    this.simpleBarRef.current.recalculate(),
                0
            );
        }

        if (prevProps.isLive !== this.props.isLive) {
            this.setState({
                heatmapHeight: this.props.isLive
                    ? this.props.heatmapDragService.minimumHeatmapHeightLive
                    : this.props.heatmapDragService.minimumHeatmapHeight
            });
        }
    }

    /**
     * @inheritdoc
     */
    componentWillUnmount() {
        this.recalcTimeout && clearTimeout(this.recalcTimeout);
        this.simpleBarRef.current &&
            this.simpleBarRef.current
                .getScrollElement()
                .removeEventListener('scroll', this.props.closeTooltip);
        this.context(this.props.id, this.resetHeight.bind(this), true);
    }

    /**
     * @inheritdoc
     */
    render() {
        const tableStyle = { width: `${100 * this.props.zoomLevel}%` },
            simpleBarClass = `Heatmap${
                this.state.isExpanded ? ' expanded' : ''
            }`,
            heatmapStyle = {
                height: `${this.state.heatmapHeight}px`
            };

        return (
            <div ref={this.heatmapContainer} className='Heatmap-container'>
                <div
                    className={`Heatmap-content ${
                        this.props.isLive ? 'live' : 'historic'
                    }`}
                >
                    <div className='HeatmapHeader-labels'>
                        <div className='HeatmapHeader-direction'>
                            {this.props.direction}
                        </div>
                        {this.props.isLive && this.props.periods[0] && (
                            <div className='HeatmapHeader-timeframe alt'>
                                {this.props.periods[0].timeframe}
                            </div>
                        )}
                        <div className='HeatmapHeader-timeframe'>Latest</div>
                    </div>
                    <SimpleBar
                        ref={this.simpleBarRef}
                        forceVisible='true'
                        autoHide='false'
                        className={simpleBarClass}
                        style={heatmapStyle}
                    >
                        <div style={tableStyle}>
                            <div className='Heatmap-latest-period'>
                                <HeatmapHeader
                                    links={this.props.links}
                                    junctions={this.props.junctions}
                                    direction={this.props.direction}
                                    totalDistance={this.props.distance}
                                    openTooltip={this.props.openJunctionTooltip}
                                    closeTooltip={
                                        this.props.closeJunctionTooltip
                                    }
                                ></HeatmapHeader>
                                {this.props.isLive && this.props.periods[0] && (
                                    <HeatmapPeriod
                                        period={this.props.periods[0]}
                                        isLatest={true}
                                        key={this.props.periods[0].timeframe}
                                        links={this.props.links}
                                        totalDistance={this.props.distance}
                                        openTooltip={this.onClickLink.bind(
                                            this
                                        )}
                                        showTimeBins={true}
                                    ></HeatmapPeriod>
                                )}
                                {this.props.isLive &&
                                    this.props.periods.length === 0 && (
                                        <div className='HeatmapPeriod'>
                                            <div className='HeatmapPeriod-timeframe'></div>
                                            <div className='HeatmapPeriod-waiting-row'>
                                                Waiting for heatmap data
                                            </div>
                                        </div>
                                    )}
                            </div>
                            <div className='Heatmap-periods-container'>
                                {this.props.periods.map(
                                    (period, index) =>
                                        (!this.props.isLive || index !== 0) && (
                                            <HeatmapPeriod
                                                period={period}
                                                key={period.timeframe}
                                                links={this.props.links}
                                                isLatest={false}
                                                totalDistance={
                                                    this.props.distance
                                                }
                                                showTimeBins={true}
                                                openTooltip={this.onClickLink.bind(
                                                    this
                                                )}
                                            ></HeatmapPeriod>
                                        )
                                )}
                            </div>
                        </div>
                    </SimpleBar>
                    <div className='Heatmap-loader hide'>
                        <div></div>
                    </div>
                </div>
                <div className='Heatmap-controls'>
                    <button
                        className='Heatmap-drag-button'
                        onMouseDown={this.onDragHandleMouseDown.bind(this)}
                    >
                        <span className='material-icons'>drag_indicator</span>
                    </button>
                </div>
            </div>
        );
    }

    /**
     * @description Handles the opening of a link tooltip on click.
     * @param {Object} event - the click event.
     * @param {Object} link - the link.
     * @param {Function} setButtonState - callback to set button state.
     */
    onClickLink(event, link, setButtonState) {
        this.simpleBarRef.current &&
            this.simpleBarRef.current
                .getScrollElement()
                .addEventListener('scroll', this.props.closeTooltip);
        this.props.openTooltip(event, link, setButtonState);
    }

    /**
     * @description Handles the closing of the tooltip.
     */
    closeTooltip() {
        this.simpleBarRef.current &&
            this.simpleBarRef.current
                .getScrollElement()
                .removeEventListener('scroll', this.props.closeTooltip);
        this.props.closeTooltip();
    }

    /**
     * @description Sets the new height of the heatmap.
     * @param {number} height - The new height.
     * @param {function} callback - A callback to run once the state has been updated.
     */
    setHeight(height, callback) {
        this.heatmapContainer.current &&
            this.heatmapContainer.current.style.setProperty(
                '--junction-line-height',
                `${height - 34}px`
            );
        this.setState(
            {
                heatmapHeight: height
            },
            callback && callback()
        );
    }

    /**
     * @description Resets the height of the heatmap.
     */
    resetHeight() {
        this.setHeight(
            this.props.isLive
                ? this.props.heatmapDragService.minimumHeatmapHeightLive
                : this.props.heatmapDragService.minimumHeatmapHeight
        );
        this.setState({
            isExpanded: false,
            isExpanding: false
        });
    }

    /**
     * @description Handles the mouse down event on the drag handle.
     * @param {Object} event - The mousedown event.
     */
    onDragHandleMouseDown(event) {
        this.setState({
            isExpanding: true,
            isExpanded: true
        });

        this.props.heatmapDragService.handleStartDragging(
            event,
            this.state.heatmapHeight,
            this.props.periods.length - 1,
            this.setHeight.bind(this),
            this.onStopDraggingHeatmap.bind(this),
            this.props.isLive
        );
    }

    /**
     * @description The callback to call when the drag has completed.
     * @param {boolean} isExpanded
     */
    onStopDraggingHeatmap(isExpanded) {
        this.setState({
            isExpanding: false,
            isExpanded: isExpanded,
            showTimeBins: isExpanded
        });
    }
}

Heatmap.contextType = ResetContext;

Heatmap.defaultProps = {
    id: '',
    periods: [],
    isLive: true,
    distance: 0,
    direction: '',
    title: '',
    links: [],
    junctions: [],
    zoomLevel: 1,
    openTooltip: () => null,
    closeTooltip: () => null,
    openJunctionTooltip: () => null,
    closeJunctionTooltip: () => null,
    heatmapDragService: {}
};

Heatmap.propTypes = {
    id: PropTypes.string.isRequired,
    periods: PropTypes.array.isRequired,
    isLive: PropTypes.bool.isRequired,
    distance: PropTypes.number.isRequired,
    direction: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    links: PropTypes.array.isRequired,
    junctions: PropTypes.array.isRequired,
    zoomLevel: PropTypes.number.isRequired,
    openTooltip: PropTypes.func.isRequired,
    closeTooltip: PropTypes.func.isRequired,
    openJunctionTooltip: PropTypes.func.isRequired,
    closeJunctionTooltip: PropTypes.func.isRequired,
    heatmapDragService: PropTypes.object.isRequired
};

export default Heatmap;
